import { CatalogFieldMapping } from '@pushly/models/lib/structs/catalogs/catalog-field-mapping'
import { CatalogItemConfig } from '@pushly/models/lib/structs/catalogs/page-analysis/catalog-item-config'
import { CatalogItemType } from '@pushly/models/lib/enums/catalogs/catalog-item-type'
import { ItemIdStrategy } from '@pushly/models/lib/enums/catalogs/item-id-strategy'

/**
 * UI representation of one item-id strategy row. The persisted shape comes
 * from CatalogItemConfig.item_id_configs[]; we add a `_displayStrategy` to
 * distinguish "JSONPATH (custom)" from "NEXT DATA" which both map to the same
 * persisted `strategy` (CONTENT_JSON_PATH) but render different field sets.
 */
export interface ILocalItemIdConfig {
    strategy: ItemIdStrategy
    segment_index?: number | null
    after_segment?: string | null
    json_path?: string | null
    regex_pattern?: string | null
    element_id?: string | null
    priority?: number | null
    _displayStrategy?: string
}

export interface ILocalCatalogConfig {
    id?: number
    enabled: boolean
    schema_type: string
    item_type?: CatalogItemType
    field_mappings: CatalogFieldMapping[]
    item_id_configs: ILocalItemIdConfig[]
}

export interface IDrawerParams {
    configIndex: number
    mapping: Partial<CatalogFieldMapping>
    mappingIndex?: number
    isRequired?: boolean
}

/** Display strategy options used by the strategy <Select>. */
export const DISPLAY_STRATEGY_JSONPATH_CUSTOM = 'jsonpath_custom'

export function createEmptyConfig(): ILocalCatalogConfig {
    return {
        enabled: true,
        schema_type: '',
        item_type: undefined,
        field_mappings: [],
        item_id_configs: [{ strategy: ItemIdStrategy.FULL_URL }],
    }
}

export function hydratedToLocal(configs: CatalogItemConfig[]): ILocalCatalogConfig[] {
    return configs.map((config) => ({
        id: config.id,
        enabled: config.enabled ?? false,
        schema_type: config.schema_type ?? '',
        item_type: config.item_type,
        field_mappings: config.field_mappings ?? [],
        item_id_configs: [
            ...(config.item_id_configs?.length
                ? config.item_id_configs.map((idConfig): ILocalItemIdConfig => {
                      // Legacy 'next_data' rows persisted before the JSONPATH-custom
                      // split. Re-hydrate them as CONTENT_JSON_PATH with element_id
                      // defaulted to __NEXT_DATA__ so they edit cleanly.
                      const strategy =
                          idConfig.strategy === ('next_data' as ItemIdStrategy)
                              ? ItemIdStrategy.CONTENT_JSON_PATH
                              : idConfig.strategy
                      return {
                          ...idConfig,
                          strategy,
                          element_id:
                              strategy === ItemIdStrategy.CONTENT_JSON_PATH
                                  ? (idConfig as any).element_id ?? '__NEXT_DATA__'
                                  : (idConfig as any).element_id,
                      }
                  })
                : [{ strategy: ItemIdStrategy.FULL_URL } as ILocalItemIdConfig]),
        ].sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0)),
    }))
}

export function getDisplayStrategy(config: ILocalItemIdConfig): string {
    if (config._displayStrategy) return config._displayStrategy
    if (config.strategy === ItemIdStrategy.CONTENT_JSON_PATH) {
        if (config.element_id != null && config.element_id !== '__NEXT_DATA__') {
            return DISPLAY_STRATEGY_JSONPATH_CUSTOM
        }
        return ItemIdStrategy.CONTENT_JSON_PATH
    }
    return config.strategy
}
